import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spin, message } from 'antd';
import _ from 'lodash';

import MainPanel from './MainPanel';
import SelectedPanel from './SelectedPanel';
import { isPromise } from '@/util/PromiseUtils';
import { knowledgeDictionaryList, knowledgePermissionBuBatchAdd } from '@/api/knowledge';

import styles from './index.module.less';

function normalizeDictRows(res) {
    if (!res || res.data == null) {
        return [];
    }
    if (res.status !== 200 && res.success !== true) {
        return [];
    }
    const raw = res.data;
    if (Array.isArray(raw)) {
        return raw;
    }
    return raw.records ?? raw.list ?? raw.rows ?? [];
}

const DepartUserTagSelector = forwardRef(
    (
        {
            showDepartTab = true,
            showUserTab = true,
            showTagTab = true,
            showAppTagTab = false,
            showAdTagTab = false,
            modalTitle,
            modalWidth = 1200,
            allowDepartIds,
            allowUsers,
            requireUserBu = false,
            onSelect,
        },
        ref
    ) => {
        const [visible, setVisible] = useState(false);
        const [title, setTitle] = useState(modalTitle);
        const [spinning, setSpinning] = useState(false);
        const [buOptions, setBuOptions] = useState([]);

        // 格式为 {departs: [{id, name}], users: [{id, name, buId?}], tags: [{id, name}]}
        const [selectedValues, setSelectedValues] = useState(null);

        useEffect(() => {
            if (!visible || !requireUserBu) {
                return undefined;
            }
            let cancelled = false;
            (async () => {
                try {
                    const res = await knowledgeDictionaryList({ dictType: 'BU' });
                    if (cancelled) {
                        return;
                    }
                    const rows = normalizeDictRows(res);
                    setBuOptions(
                        rows.map(item => ({
                            label: item.dictLabel,
                            value: item.id,
                        }))
                    );
                } catch {
                    if (!cancelled) {
                        setBuOptions([]);
                    }
                }
            })();
            return () => {
                cancelled = true;
            };
        }, [visible, requireUserBu]);

        /**
         * 打开 Modal，同时可以传入的相应的参数
         *
         * @param modalTitle Modal 标题，不传，则使用 props 中的值
         * @param initialValues 初始选择项，格式为 {departs: [{id, name}], users: [{id, name}], tags: [{id, name}]}
         */
        function showModal({ modalTitle, initialValues } = {}) {
            setVisible(true);
            if (modalTitle) {
                setTitle(modalTitle);
            }
            if (!_.isEmpty(initialValues)) {
                const { departs, users, tags, apptags, adtags } = initialValues;
                setSelectedValues({ departs, users, tags, apptags, adtags });
            } else {
                setSelectedValues(null);
            }
        }

        function closeModal() {
            setVisible(false);
            setSpinning(false);
        }

        useImperativeHandle(ref, () => ({
            showModal,
            closeModal,
        }));

        function onAddSelected(type, { id, name, buId }) {
            let values = _.get(selectedValues, type, []);
            const value = values.find(item => item.id === id);
            if (!value) {
                const item = { id, name };
                if (buId != null && buId !== '') {
                    item.buId = buId;
                }
                values = [...values, item];
                setSelectedValues({ ...(selectedValues || {}), [type]: values });
            }
        }

        /**
         * 某些 tab 下使用复选框，可能比较复杂，所以提供此方法用于一次更新整个 type 的列表。
         *
         * @param type
         * @param values
         */
        function onReplaceSelectedByType(type, values) {
            setSelectedValues({ ...selectedValues, [type]: values });
        }

        function onDeleteSelected(type, delId) {
            let values = _.get(selectedValues, type, []);
            values = values.filter(({ id }) => id !== delId);
            setSelectedValues({ ...selectedValues, [type]: values });
        }

        function onUpdateSelected(type, id, patch) {
            const values = _.get(selectedValues, type, []);
            const next = values.map(item => (item.id === id ? { ...item, ...patch } : item));
            setSelectedValues({ ...selectedValues, [type]: next });
        }

        function buildSubmitValues() {
            const values = {};
            Object.keys(selectedValues || {}).forEach(key => {
                const arr = selectedValues[key];
                values[key] = arr || [];
            });
            return values;
        }

        function validateUserBu(values) {
            if (!requireUserBu) {
                return true;
            }
            const users = values.users || [];
            if (users.length === 0) {
                return true;
            }
            const missing = users.find(user => !user.buId);
            if (missing) {
                message.warning(`请为员工「${missing.name}」选择BU`);
                return false;
            }
            return true;
        }

        function batchAddUserBuPermission(values) {
            const users = values.users || [];
            if (!requireUserBu || users.length === 0) {
                return Promise.resolve(true);
            }
            return knowledgePermissionBuBatchAdd(
                users.map(({ id, buId }) => ({
                    userId: id,
                    buId,
                }))
            ).then(res => {
                if (res.status === 200 || res.success === true) {
                    return true;
                }
                message.error(res.message || 'BU权限添加失败');
                return false;
            });
        }

        function handleSelectSuccess(values) {
            return batchAddUserBuPermission(values).then(ok => {
                if (ok) {
                    closeModal();
                } else {
                    setSpinning(false);
                }
            });
        }

        function onOk() {
            if (_.isEmpty(selectedValues)) {
                closeModal();
                return;
            }

            const values = buildSubmitValues();
            if (!validateUserBu(values)) {
                return;
            }

            setSpinning(true);
            const result = onSelect(values);

            if (isPromise(result)) {
                result
                    .then(data => {
                        if (data === true) {
                            return handleSelectSuccess(values);
                        }
                        setSpinning(false);
                        return undefined;
                    })
                    .catch(() => {
                        setSpinning(false);
                    });
            } else if (result) {
                handleSelectSuccess(values);
            } else {
                setSpinning(false);
            }
        }

        return (
            <Modal
                destroyOnClose
                title={title}
                visible={visible}
                onCancel={closeModal}
                onOk={onOk}
                width={modalWidth}
            >
                <Spin spinning={spinning}>
                    <div className={styles.container}>
                        <div className={styles.mainPanel}>
                            <MainPanel
                                selectedValues={selectedValues}
                                onAddSelected={onAddSelected}
                                onReplaceSelectedByType={onReplaceSelectedByType}
                                allowDepartIds={allowDepartIds}
                                allowUsers={allowUsers}
                                showDepartTab={showDepartTab}
                                showUserTab={showUserTab}
                                showTagTab={showTagTab}
                                showAppTagTab={showAppTagTab}
                                showAdTagTab={showAdTagTab}
                            />
                        </div>
                        <div className={styles.selectedPanel}>
                            <SelectedPanel
                                selectedValues={selectedValues}
                                onDeleteSelected={onDeleteSelected}
                                requireUserBu={requireUserBu}
                                buOptions={buOptions}
                                onUpdateSelected={onUpdateSelected}
                            />
                        </div>
                    </div>
                </Spin>
            </Modal>
        );
    }
);

DepartUserTagSelector.displayName = 'DepartUserTagSelector';

DepartUserTagSelector.propTypes = {
    /**
     * 是否显示部门选择 tab
     */
    showDepartTab: PropTypes.bool,

    /**
     * 是否显示人员选择 tab
     */
    showUserTab: PropTypes.bool,

    /**
     * 是否显示标签选择 tab
     */
    showTagTab: PropTypes.bool,

    /**
     * 是否显示 AD 应用标签 tab，默认为 false
     */
    showAppTagTab: PropTypes.bool,

    /**
     * 是否显示 AD 非应用标签 tab
     */
    showAdTagTab: PropTypes.bool,

    modalTitle: PropTypes.string,

    modalWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /**
     * 可见的部门 id
     *
     * 如果为为 null 或 undefined, 表示所有部门可见
     *
     * 如果是 [] ，表示所有不可见
     * 如果有值，则对应部门和子部门可见
     */
    allowDepartIds: PropTypes.arrayOf(PropTypes.string),

    allowUsers: PropTypes.array,

    /**
     * 已选员工是否必须选择 BU，并在确定成功后批量新增 BU 权限
     */
    requireUserBu: PropTypes.bool,

    /**
     * 点击「确定」按钮后触发，返回 true 的时候关闭 Modal
     *
     * 支持返回 Promise
     */
    onSelect: PropTypes.func.isRequired,
};

export default DepartUserTagSelector;
